<template>
    <PlatformAccountsHeader
        v-model:show-add-account-modal="showAddAccountModal"
        :is-loading="operationPending"
        :publish-meta-accounts-status="publishMetaAccountsStatus"
        @saveChanges="onSaveChanges"
        @publishChanges="onPublishChanges"
    />
    <div class="accounts-wrap">
        <hox-loading-layer v-if="operationPending || isLoading" :message="statusMessage" />
        <div v-else-if="hasActiveAccounts && visibleAccounts" class="draggable-wrapper">
            <div v-if="!publishMetaAccountsStatus.canPublish" class="ivu-alert-warning">
                {{ publishMetaAccountsStatus.message }}
                <ul v-if="publishMetaAccountsStatus.extraInfo" class="accounts-error-list">
                    <li v-for="(group, index) in publishMetaAccountsStatus.extraInfo" :key="index">
                        <strong>{{ group.label }}</strong>
                        <ul>
                            <li v-for="(msg, i) in group.errors" :key="i">
                                <strong>{{ msg.editableGroup }}</strong> {{ msg.error }}
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
            <Draggable
                :model-value="visibleAccounts"
                handle=".connect-accounts-drag-handle"
                item-key="platformAccount.name"
                @update="changeAccountOrder"
            >
                <template #item="{ element, index }">
                    <PlatformAccount
                        :account="element"
                        :index="index"
                        @showVariantsSelect="emit('showVariantsSelect', $event)"
                    />
                </template>
            </Draggable>
        </div>

        <hox-empty-message v-else display-image-first type="no-content-icon">
            <template #title>
                <strong>You don't have any signals</strong>
            </template>
            <template #content>
                <p>Add your platform to create new signals for managing the timing and delivery of your campaigns.</p>
            </template>

            <template #actionItems>
                <Button type="primary" @click="showAddAccountModal = true">+ Add Meta Account</Button>
            </template>
        </hox-empty-message>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import Draggable from "vuedraggable";

import PlatformAccountsHeader from "@/Connect/components/Accounts/PlatformAccountsListHeader.vue";
import PlatformAccount from "@/Connect/components/Accounts/PlatformAccount.vue";

import { apolloClient } from "@/apollo";
import CreateCampaignFileMutation from "@/apollo/mutations/CreateCampaignFile.gql";

import {
    getCampaignPublishSummary,
    getDesiredState,
    publishCampaign,
    saveCampaign,
    validateCampaign
} from "@/utils/CreativeIntelligenceApi/connect";

import { useConnectStore } from "@/Connect/store";
import { snackbar } from "@/utils";

const route = useRoute();
const campaignId = route.params.campaignId as string;

const connectStore = useConnectStore();

const { isLoading, publishMetaAccountsStatus } = defineProps<{
    isLoading: boolean;
    publishMetaAccountsStatus: {
        canPublish: boolean;
        message: string;
        extraInfo: {
            label: string;
            errors: {
                editableGroup: string;
                error: string;
            }[];
        }[];
    };
}>();

const emit = defineEmits<{
    (e: "showVariantsSelect", args: { platformAccountName: string; ruleName: string }): void;
    (e: "fetchUpdatedCampaignInfo"): void;
}>();

/** Local State */
const showAddAccountModal = ref(false);
const operationPending = ref(false);
// const publishStatusLoading = ref(true);

const saveStatus = ref<
    "export" | "validate" | "save" | "saveRules" | "desiredState" | "publish" | "getPublishStatus" | ""
>("");
const tempCampaignFileLocation = ref("");

/** COMPUTED */
const visibleAccounts = computed(() => connectStore.visibleAccounts);
const hasActiveAccounts = computed(() => connectStore.hasActiveAccounts);

const statusMessage = computed(() => {
    switch (saveStatus.value) {
        case "export":
            return "Exporting campaign data";

        case "validate":
            return "Validating campaign rules";

        case "save":
            return "Saving campaign rules";

        case "saveRules":
            return "Saving campaign rules";

        case "getPublishStatus":
            return "Getting campaign publish status";

        default:
            return "";
    }
});

/** METHODS */
function handleErrorResponse(error: Error) {
    snackbar.error(error?.message || "There was an error please try again.");
}

function changeAccountOrder({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    connectStore.changeAccountOrder(oldIndex, newIndex);
}

function getCampaignInfo() {
    emit("fetchUpdatedCampaignInfo");
}

/** DATA FETCHING */
async function getCampaignPublishStatus() {
    try {
        const { data } = await getCampaignPublishSummary({
            campaignId
        });

        connectStore.setPublishSummary(data);
    } catch (e) {
        handleErrorResponse(e as Error);
    }
}

async function saveChanges(publishing?: boolean) {
    operationPending.value = true;

    saveStatus.value = "export";

    const { data: createCampaignFileData } = await apolloClient.mutate({
        mutation: CreateCampaignFileMutation,
        variables: { campaignId }
    });

    if (!createCampaignFileData.createCampaignFile) {
        throw new Error("There was an error creating the campaign file");
    }

    if (!connectStore.accounts) {
        throw new Error("There was an error getting the accounts");
    }

    tempCampaignFileLocation.value = createCampaignFileData.createCampaignFile;

    saveStatus.value = "validate";

    const { data: validateData } = await validateCampaign({
        campaignId: campaignId,
        campaignData: {
            latestVersionId: connectStore.latestVersionId,
            accountsRules: connectStore.accounts,
            fileUrl: tempCampaignFileLocation.value
        }
    });

    if (!validateData) {
        throw new Error("There was an error validating the campaign rules");
    }

    if (publishing) {
        saveStatus.value = "saveRules";
    } else {
        saveStatus.value = "save";
    }

    return await saveCampaign({
        campaignId,
        campaignData: {
            ...validateData,
            latestVersionId: connectStore.latestVersionId
        }
    });
}

async function onSaveChanges() {
    operationPending.value = true;

    try {
        await saveChanges();

        snackbar.success("Campaign is saved");
        getCampaignInfo();
    } catch (e) {
        handleErrorResponse(e as Error);
    } finally {
        saveStatus.value = "";
        operationPending.value = false;
    }
}

async function pollPublishStatusUntilSynced(maxRetries = 5, intervalMs = 2000) {
    let retries = 0;
    let allVisibleAccountsInSummary = false;

    while (retries < maxRetries) {
        await getCampaignPublishStatus();
        allVisibleAccountsInSummary = (connectStore.visibleAccountsWithActiveRules ?? []).every(acc => {
            const platformAccountId = acc.platformAccount.id;
            return Object.values(connectStore.publishSummary).some(summary => summary!.accountId === platformAccountId);
        });

        if (allVisibleAccountsInSummary) {
            break;
        }
        retries++;
        if (retries < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
    }

    if (retries === maxRetries && !allVisibleAccountsInSummary) {
        snackbar.error("Failed to get publish status for all accounts. Please refresh the page or try again later.");
    }
}

async function onPublishChanges() {
    operationPending.value = true;

    try {
        await saveChanges(true);

        saveStatus.value = "desiredState";

        await getDesiredState({
            campaignId
        });

        saveStatus.value = "publish";

        const { data: publishCampaignData } = await publishCampaign({
            campaignId: campaignId
        });

        const isEmptyCampaign = publishCampaignData.publishStatuses.every(
            publishStatus => publishStatus.account.status === "deleted"
        );

        if (isEmptyCampaign) {
            snackbar.info("Campaign has no active accounts to publish");
        } else {
            snackbar.success("Campaign is being published");
        }

        getCampaignInfo();
        saveStatus.value = "getPublishStatus";
        await pollPublishStatusUntilSynced();
    } catch (e) {
        handleErrorResponse(e as Error);
    } finally {
        saveStatus.value = "";
        operationPending.value = false;
    }
}

/** INIT */
onMounted(() => {
    getCampaignPublishStatus();
});
</script>

<style lang="scss" scoped>
.accounts-wrap {
    /* to account for the fixed header */
    padding-top: 60px;
    height: 100%;

    display: flex;
    align-items: center;
    justify-items: center;
}

.draggable-wrapper {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    padding: 48px;

    gap: 20px;
}

.ivu-alert-warning {
    padding: 5px;
}

.accounts-error-list {
    ul {
        padding-left: 10px;
    }
    li {
        padding: 0;
        list-style: none;
    }
}
</style>
